import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Copy, Lock, MessageSquare, Save, X } from "lucide-react";
import { apiFetch, APIError } from "@/lib/api-client";
import { useAuth } from "@/features/auth/auth-context";
import { toast } from "@/components/ui/toaster";
import { HeatBadge } from "@/components/ui/heat-badge";
import { ELTERN_TEMPLATES } from "@/lib/notification-templates";
import { cn } from "@/lib/utils";

/**
 * ElternPushTrigger / ElternPushModal — phase 13 C.3.
 *
 * Header-action-button + modal mit stufen-tabs, live-preview und
 * org-override-speicherung (admin-only). Modal nutzt nativen <dialog>
 * für focus-trap + ESC-close + backdrop-inert (keine neue dep).
 *
 * Templates kommen vom backend (GET /v1/org/push-templates merged
 * org+system-defaults) mit client-side fallback auf ELTERN_TEMPLATES
 * wenn backend nicht erreichbar (offline-resilience).
 *
 * Live-preview: useDeferredValue für native debounce. Placeholder
 * {{org_name}} wird client-side gerendert (server-render via /push/render
 * ist verfügbar aber für nur-copy-to-clipboard unnötig).
 */

type PushLevel = 2 | 3 | 4;

interface PushTemplate {
  level: PushLevel;
  title: string;
  body: string;
  is_default: boolean;
}

interface ListResponse {
  templates: PushTemplate[];
}

const LEVEL_LABEL: Record<PushLevel, string> = {
  2: "Soll",
  3: "Muss",
  4: "Kritisch",
};

function currentLevelToPush(level: "soll" | "muss" | "kritisch"): PushLevel {
  if (level === "soll") return 2;
  if (level === "muss") return 3;
  return 4;
}

export interface ElternPushTriggerProps {
  className?: string;
  currentLevel?: "normal" | "soll" | "muss" | "kritisch";
}

export function ElternPushTrigger({
  className,
  currentLevel = "normal",
}: ElternPushTriggerProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const disabled = currentLevel === "normal";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={disabled ? "Bei Stufe 1 ist keine Eltern-Info erforderlich" : undefined}
        className={cn(
          "inline-flex h-10 shrink-0 items-center gap-2 rounded-md",
          "border border-[color:var(--color-border)] bg-white px-3",
          "text-sm font-medium text-[color:var(--color-text-primary)]",
          "transition-colors duration-150 ease-out",
          "hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-surface-sunken)]",
          "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white",
          className,
        )}
      >
        <MessageSquare
          aria-hidden="true"
          strokeWidth={2}
          className="size-4 shrink-0 text-[color:var(--color-text-secondary)]"
        />
        <span className="whitespace-nowrap">Eltern-Info</span>
      </button>
      {open && (
        <ElternPushModal
          initialLevel={disabled ? 2 : currentLevelToPush(currentLevel as "soll" | "muss" | "kritisch")}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

interface ModalProps {
  initialLevel: PushLevel;
  onClose: () => void;
}

interface FormData {
  title: string;
  body: string;
}

function ElternPushModal({ initialLevel, onClose }: ModalProps): React.ReactElement {
  const dialogRef = React.useRef<HTMLDialogElement>(null);
  const qc = useQueryClient();
  const auth = useAuth();
  const [activeLevel, setActiveLevel] = React.useState<PushLevel>(initialLevel);
  const [copyState, setCopyState] = React.useState<"idle" | "ok" | "fail">("idle");
  const orgName = auth.user?.email?.split("@")[1] ?? "Unsere Einrichtung";
  const isAdmin = auth.user?.role === "admin";

  const templates = useQuery({
    queryKey: ["org", "push-templates"],
    queryFn: () => apiFetch<ListResponse>("/v1/org/push-templates"),
    staleTime: 60 * 1000,
  });

  // Aktives template (server-override → fallback ELTERN_TEMPLATES).
  const activeTemplate = React.useMemo<PushTemplate>(() => {
    const found = templates.data?.templates.find((t) => t.level === activeLevel);
    if (found) return found;
    const fallback = ELTERN_TEMPLATES[activeLevel];
    return { level: activeLevel, title: fallback.title, body: fallback.body, is_default: true };
  }, [templates.data, activeLevel]);

  const form = useForm<FormData>({
    defaultValues: { title: activeTemplate.title, body: activeTemplate.body },
  });

  // Reset form bei tab-wechsel oder template-reload.
  React.useEffect(() => {
    form.reset({ title: activeTemplate.title, body: activeTemplate.body });
  }, [activeTemplate.title, activeTemplate.body, form]);

  // Dialog open + close-handler.
  React.useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (!dlg.open) dlg.showModal();
    const handleClose = () => onClose();
    dlg.addEventListener("close", handleClose);
    return () => dlg.removeEventListener("close", handleClose);
  }, [onClose]);

  // Backdrop-click schließt (target === dialog wenn auf backdrop geklickt).
  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) {
      dialogRef.current?.close();
    }
  };

  const liveTitle = form.watch("title");
  const liveBody = form.watch("body");
  const previewTitle = React.useDeferredValue(liveTitle);
  const previewBody = React.useDeferredValue(liveBody);

  const renderedTitle = React.useMemo(
    () => previewTitle.replace(/\{\{org_name\}\}/g, orgName),
    [previewTitle, orgName],
  );
  const renderedBody = React.useMemo(
    () => previewBody.replace(/\{\{org_name\}\}/g, orgName),
    [previewBody, orgName],
  );

  const save = useMutation({
    mutationFn: async (data: FormData) =>
      apiFetch("/v1/org/push-templates", {
        method: "POST",
        body: { level: activeLevel, title: data.title, body: data.body },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org", "push-templates"] });
      toast("Vorlage als Org-Default gespeichert", "success");
    },
    onError: (err) => {
      if (err instanceof APIError) {
        toast(`Speichern fehlgeschlagen: ${err.code}`, "danger");
      } else {
        toast("Speichern fehlgeschlagen", "danger");
      }
    },
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${renderedTitle}\n\n${renderedBody}`);
      setCopyState("ok");
      window.setTimeout(() => setCopyState("idle"), 2000);
    } catch {
      setCopyState("fail");
    }
  };

  return (
    <dialog
      ref={dialogRef}
      onClick={handleBackdropClick}
      aria-labelledby="eltern-push-title"
      aria-modal="true"
      className={cn(
        "w-full max-w-2xl rounded-xl border border-[color:var(--color-border)]",
        "bg-[color:var(--color-surface)] p-0 shadow-[var(--shadow-lg)]",
        "backdrop:bg-black/40 backdrop:backdrop-blur-sm",
        "max-sm:m-0 max-sm:mt-auto max-sm:max-h-[90vh] max-sm:w-full max-sm:max-w-none",
        "max-sm:rounded-b-none max-sm:rounded-t-xl",
      )}
    >
      <header className="flex items-start justify-between gap-3 border-b border-[color:var(--color-border)] p-5">
        <div>
          <h2
            id="eltern-push-title"
            className="text-xl font-semibold text-[color:var(--color-text-primary)]"
          >
            Eltern-Info
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-text-secondary)]">
            Vorlage anpassen, kopieren oder als Org-Default speichern.
          </p>
        </div>
        <button
          type="button"
          onClick={() => dialogRef.current?.close()}
          aria-label="Dialog schließen"
          className="-m-1 inline-flex size-10 shrink-0 items-center justify-center rounded-md text-[color:var(--color-text-secondary)] hover:bg-[color:var(--color-surface-sunken)]"
        >
          <X aria-hidden="true" strokeWidth={2} className="size-5" />
        </button>
      </header>

      <div className="flex flex-col gap-5 p-5 md:grid md:grid-cols-2 md:gap-6">
        <form
          onSubmit={form.handleSubmit((data) => save.mutate(data))}
          className="flex flex-col gap-4"
        >
          <div role="tablist" aria-label="Hitzestufe wählen" className="flex gap-2">
            {([2, 3, 4] as const).map((lvl) => {
              const isActive = lvl === activeLevel;
              // B3: HeatBadge nutzt DB-Werte 0-3. push-template nutzt 2-4 (altes schema).
              // Bridge: template-level -1 → DB-Wert (2→1=Soll, 3→2=Muss, 4→3=Kritisch).
              const badgeLevel = (lvl - 1) as 1 | 2 | 3;
              return (
                <button
                  key={lvl}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveLevel(lvl)}
                  className={cn(
                    "inline-flex flex-1 items-center justify-center gap-2 rounded-md",
                    "h-11 px-3 text-sm font-medium",
                    "transition-colors duration-150 ease-out",
                    isActive
                      ? "border-2 border-[color:var(--color-brand-primary)] bg-[color:var(--color-brand-primary-soft)] text-[color:var(--color-text-primary)]"
                      : "border border-[color:var(--color-border)] bg-white text-[color:var(--color-text-secondary)] hover:bg-[color:var(--color-surface-sunken)]",
                  )}
                >
                  <HeatBadge level={badgeLevel} compact />
                  <span className="whitespace-nowrap">{LEVEL_LABEL[lvl]}</span>
                </button>
              );
            })}
          </div>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[color:var(--color-text-primary)]">Titel</span>
            <input
              type="text"
              maxLength={200}
              className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3 text-base"
              {...form.register("title", { required: true })}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[color:var(--color-text-primary)]">
              Nachrichtentext
            </span>
            <textarea
              rows={8}
              maxLength={2000}
              className="w-full rounded-md border border-[color:var(--color-border)] bg-white px-3 py-2 text-base leading-normal"
              {...form.register("body", { required: true })}
            />
            <span className="text-xs text-[color:var(--color-text-secondary)]">
              Platzhalter <code>{"{{org_name}}"}</code> wird ersetzt.
            </span>
          </label>

          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handleCopy}
              className={cn(
                "inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-md",
                "bg-[color:var(--color-brand-primary-strong)] px-4",
                "text-sm font-semibold text-[color:var(--color-text-on-primary)]",
                "transition-colors duration-150 ease-out",
                "hover:bg-[color:var(--color-warm-orange-800)]",
              )}
            >
              <Copy aria-hidden="true" strokeWidth={2.25} className="size-4" />
              {copyState === "ok" ? "Kopiert" : "In Zwischenablage"}
            </button>
            {isAdmin ? (
              <button
                type="submit"
                disabled={save.isPending}
                className={cn(
                  "inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-md",
                  "border border-[color:var(--color-border)] bg-white px-4",
                  "text-sm font-medium text-[color:var(--color-text-primary)]",
                  "transition-colors duration-150 ease-out",
                  "hover:bg-[color:var(--color-surface-sunken)]",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                )}
              >
                <Save aria-hidden="true" strokeWidth={2} className="size-4" />
                {save.isPending ? "Speichere …" : "Als Org-Default"}
              </button>
            ) : (
              <span
                aria-disabled="true"
                title="Nur Admins können Vorlagen als Default speichern"
                className={cn(
                  "inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-md",
                  "border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4",
                  "text-sm font-medium text-[color:var(--color-text-muted)]",
                  "cursor-not-allowed",
                )}
              >
                <Lock aria-hidden="true" strokeWidth={2} className="size-4" />
                Speichern (nur Admin)
              </span>
            )}
          </div>
          {copyState === "fail" && (
            <p role="alert" className="text-xs text-[color:var(--color-danger)]">
              Kopieren fehlgeschlagen — Bitte Text manuell markieren.
            </p>
          )}
        </form>

        <aside
          aria-label="Vorschau"
          className={cn(
            "rounded-lg border border-[color:var(--color-border)]",
            "bg-[color:var(--color-surface-elevated)] p-4",
          )}
        >
          <p className="text-xs font-medium uppercase tracking-[var(--letter-spacing-wide)] text-[color:var(--color-text-secondary)]">
            Vorschau
          </p>
          <h3 className="mt-2 text-base font-semibold text-[color:var(--color-text-primary)]">
            {renderedTitle || "(Titel fehlt)"}
          </h3>
          <p className="mt-2 whitespace-pre-line text-sm leading-normal text-[color:var(--color-text-primary)]">
            {renderedBody || "(Nachrichtentext fehlt)"}
          </p>
        </aside>
      </div>
    </dialog>
  );
}

ElternPushTrigger.displayName = "ElternPushTrigger";
